import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Send, MessageCircle, Facebook, Instagram, Building2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { usePageTitle } from '../hooks/usePageTitle';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { useT } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = () => {
  usePageTitle('Contact Us');
  const settings = useSiteSettings();
  const tr = useT();
  const contactEmail = settings.contact_email || 'info@rishidevfoundation.in';

  const registeredAddress = settings.registered_address || '';
  const adminAddress = settings.admin_address || '';
  const displayPref = settings.contact_page_address_display || 'registered';

  const addressBlocks = [];
  if (displayPref === 'registered' || displayPref === 'both') {
    if (registeredAddress) addressBlocks.push({ label: tr('Registered Office'), text: registeredAddress, testId: 'contact-registered-address' });
  }
  if (displayPref === 'admin' || displayPref === 'both') {
    if (adminAddress) addressBlocks.push({ label: tr('Administrative Office'), text: adminAddress, testId: 'contact-admin-address' });
  }
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/contact`, formData);
      toast.success('Thank you! We will get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewsletter = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/newsletter/subscribe`, { email: newsletterEmail });
      toast.success('Successfully subscribed to our newsletter!');
      setNewsletterEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="text-saffron-600 uppercase text-xs md:text-sm tracking-[0.2em] font-body font-semibold mb-4">
              {tr('Get In Touch')}
            </div>
            <h1 className="text-4xl md:text-6xl font-heading font-bold text-charcoal-800 tracking-tighter leading-none mb-6">
              {tr('Contact Us')}
            </h1>
            <p className="text-lg md:text-xl text-charcoal-600 font-body leading-relaxed max-w-3xl mx-auto">
              {tr("We're here to answer your questions and guide you on your spiritual journey.")}
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl md:text-3xl font-heading text-charcoal-800 mb-8">
                {tr('Send Us a Message')}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label className="block text-sm font-body font-medium text-charcoal-700 mb-2">
                    {tr('Name *')}
                  </label>
                  <input
                    type="text"
                    required
                    data-testid="contact-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body"
                  />
                </div>
                <div>
                  <label className="block text-sm font-body font-medium text-charcoal-700 mb-2">
                    {tr('Email *')}
                  </label>
                  <input
                    type="email"
                    required
                    data-testid="contact-email-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body"
                  />
                </div>
                <div>
                  <label className="block text-sm font-body font-medium text-charcoal-700 mb-2">
                    {tr('Phone')}
                  </label>
                  <input
                    type="tel"
                    data-testid="contact-phone-input"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body"
                  />
                </div>
                <div>
                  <label className="block text-sm font-body font-medium text-charcoal-700 mb-2">
                    {tr('Message *')}
                  </label>
                  <textarea
                    required
                    rows={5}
                    data-testid="contact-message-input"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body resize-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="w-full bg-saffron-600 text-white rounded-full px-8 py-4 font-body font-medium hover:bg-saffron-500 transition-colors shadow-lg inline-flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {submitting ? tr('Sending...') : tr('Send Message')} <Send size={20} />
                </button>
              </form>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl md:text-3xl font-heading text-charcoal-800 mb-8">
                {tr('Connect With Us')}
              </h2>
              <div className="space-y-6 mb-12">
                <a href="tel:+919895959558" className="flex items-start gap-4 group" data-testid="contact-phone">
                  <div className="w-12 h-12 bg-saffron-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-saffron-200 transition-colors">
                    <Phone className="text-saffron-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading text-charcoal-800 mb-1">{tr('Phone')}</h3>
                    <p className="text-charcoal-600 group-hover:text-saffron-600 transition-colors">+91 98959 59558</p>
                  </div>
                </a>
                <a
                  href="https://wa.me/919895959558?text=Namaste!%20I%20would%20like%20to%20know%20more%20about%20Rishidev%20Foundation."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 group"
                  data-testid="contact-whatsapp"
                >
                  <div className="w-12 h-12 bg-[#25D366]/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-[#25D366]/20 transition-colors">
                    <MessageCircle className="text-[#25D366]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading text-charcoal-800 mb-1">{tr('WhatsApp')}</h3>
                    <p className="text-charcoal-600 group-hover:text-[#25D366] transition-colors">
                      {tr('Chat with us instantly')}
                    </p>
                  </div>
                </a>
                <a
                  href="https://t.me/rishidevfoundation"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 group"
                  data-testid="contact-telegram"
                >
                  <div className="w-12 h-12 bg-[#0088CC]/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-[#0088CC]/20 transition-colors">
                    <Send className="text-[#0088CC]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading text-charcoal-800 mb-1">{tr('Telegram')}</h3>
                    <p className="text-charcoal-600 group-hover:text-[#0088CC] transition-colors">
                      {tr('Join our channel')} @rishidevfoundation
                    </p>
                  </div>
                </a>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-saffron-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="text-saffron-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading text-charcoal-800 mb-1">{tr('Email')}</h3>
                    <a
                      href={`mailto:${contactEmail}`}
                      data-testid="contact-email-value"
                      className="text-charcoal-600 hover:text-saffron-600 transition-colors"
                    >
                      {contactEmail}
                    </a>
                  </div>
                </div>
                {addressBlocks.map((block) => (
                  <div key={block.testId} className="flex items-start gap-4" data-testid={block.testId}>
                    <div className="w-12 h-12 bg-saffron-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Building2 className="text-saffron-600" size={24} />
                    </div>
                    <div>
                      <h3 className="text-lg font-heading text-charcoal-800 mb-1">{block.label}</h3>
                      <p className="text-charcoal-600 whitespace-pre-line leading-relaxed">{tr(block.text)}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-3 mb-8">
                <a
                  href="https://www.facebook.com/profile.php?id=61568203162668"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-facebook"
                  className="flex-1 flex items-center justify-center gap-2 bg-[#1877F2] text-white rounded-lg px-4 py-3 hover:opacity-90 transition-opacity font-body font-medium"
                >
                  <Facebook size={20} /> Facebook
                </a>
                <a
                  href="https://www.instagram.com/rishidevfoundation/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-instagram"
                  className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-br from-[#E1306C] to-[#F77737] text-white rounded-lg px-4 py-3 hover:opacity-90 transition-opacity font-body font-medium"
                >
                  <Instagram size={20} /> Instagram
                </a>
              </div>

              <div className="bg-gradient-to-br from-saffron-50 to-gold-100 rounded-xl p-8">
                <h3 className="text-xl font-heading text-charcoal-800 mb-4">
                  {tr('Subscribe to Our Newsletter')}
                </h3>
                <p className="text-sm text-charcoal-600 mb-6">
                  {tr('Receive spiritual insights, updates on events, and exclusive teachings.')}
                </p>
                <form onSubmit={handleNewsletter} className="flex gap-2" data-testid="newsletter-form">
                  <input
                    type="email"
                    required
                    placeholder={tr('Your email')}
                    data-testid="newsletter-email-input"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    className="flex-1 px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body"
                  />
                  <button
                    type="submit"
                    data-testid="newsletter-submit-button"
                    className="bg-saffron-600 text-white rounded-lg px-6 py-3 font-body font-medium hover:bg-saffron-500 transition-colors"
                  >
                    {tr('Subscribe')}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
